import React from 'react';

import {
	Select as TempoSelect,
	type SelectProps as TempoSelectProps,
} from '@tempo/core';

type Primitive = string | number;

export type SelectOption = {
	label: string;
	value: Primitive;
};

export type SelectProps = Omit<
	TempoSelectProps,
	'value' | 'defaultValue' | 'onChange'
> & {
	options?: SelectOption[];
	value?: Primitive;
	defaultValue?: Primitive;
	placeholder?: string;
	onChange?: (value: string) => void;
	isLoading?: boolean;
	isMulti?: boolean;
};

const EMPTY_VALUE = '';

const Select = ({
	options = [],
	value,
	defaultValue,
	placeholder,
	onChange,
	size = 'medium',
	variant = 'outline',
	isDisabled,
	isLoading,
	isMulti = false,
	sx,
	...rest
}: SelectProps) => {
	const normalizedValue =
		value !== undefined && value !== null ? String(value) : EMPTY_VALUE;

	const normalizedDefaultValue =
		defaultValue !== undefined && defaultValue !== null
			? String(defaultValue)
			: EMPTY_VALUE;

	const placeholderLabel = isLoading ? 'Loading...' : placeholder;
	const shouldShowPlaceholder = Boolean(placeholderLabel);
	const isPlaceholderDisplayed = normalizedValue === EMPTY_VALUE;

	const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		onChange?.(event.target.value);
	};

	return (
		<TempoSelect
			size={size}
			variant={variant}
			value={normalizedValue}
			defaultValue={value === undefined ? normalizedDefaultValue : undefined}
			onChange={handleChange}
			isDisabled={isDisabled || isLoading}
			sx={{
				color: isPlaceholderDisplayed
					? 'text.neutral.secondary.default'
					: undefined,
				...sx,
			}}
			{...rest}
		>
			{shouldShowPlaceholder && (
				<option value="" disabled hidden={!isMulti}>
					{placeholderLabel}
				</option>
			)}

			{options.map((option, index) => (
				<option
					data-testid={`select-option-${option.value}`}
					key={`${String(option.value)}-${index}`}
					value={String(option.value)}
				>
					{option.label}
				</option>
			))}
		</TempoSelect>
	);
};

export default Select;
